<script>
  // `mapValues` stores its pairs as an object, which cannot represent a row whose
  // key has not been typed yet — so the ordered list here is the editing model and
  // the object is derived from it. Rows with a blank key are shown but not saved,
  // which is what lets "Add a match" put an empty row on screen at all.
  //
  // Seeded once per mounted step: the parent keys this component on the column and
  // the step's position and function, so it re-seeds when the analyst switches
  // column or changes what this step does, and survives its own keystrokes.
  import Button from './Button.svelte';
  import { schemeConcepts, sourceValueOptions } from '../../../lib/mapping_workbench.js';

  let { step, column, rows: sampleRows = [], scheme = null, onedit } = $props();

  // svelte-ignore state_referenced_locally -- seeding once is the point; see above
  let rows = $state(Object.entries(step.map ?? {}));

  const sourceOptions = $derived(sourceValueOptions(sampleRows, column.source, step.map));
  const concepts = $derived(schemeConcepts(scheme));
  const save = (patch = {}) => onedit({ map: Object.fromEntries(rows.filter(([from]) => from.trim() !== '')), ...patch });
  const setRow = (at, from, to) => {
    rows[at] = [from, to];
    save(to && scheme?.output ? { output: scheme.output } : {});
  };
  const addRow = () => { rows = [...rows, ['', '']]; };
  const addSource = (value) => { rows = [...rows, [value, '']]; save(); };
  const removeRow = (at) => { rows = rows.filter((_, j) => j !== at); save(); };

  const blank = $derived(rows.some(([from]) => from.trim() === ''));
  const duplicate = $derived.by(() => {
    const keys = rows.map(([from]) => from.trim()).filter(Boolean);
    return keys.length !== new Set(keys).size;
  });
</script>

{#if sourceOptions.length}
  <div class="mapping-options" aria-label="Values found in the file">
    <p class="wf2-caption"><strong>Values found in the file</strong> · choose an unmapped value to add it</p>
    <div class="wf2-row" style="gap:6px; flex-wrap:wrap">
      {#each sourceOptions as option}
        <button class="value-chip" disabled={option.mapped}
                aria-label={`${option.value}, appears ${option.count} times${option.mapped ? ', mapped' : ''}`}
                onclick={() => addSource(option.value)}>
          {option.value}{option.mapped ? ' ✓' : ''}
        </button>
      {/each}
    </div>
  </div>
{/if}

<datalist id="mapping-source-values">
  {#each sourceOptions as option}<option value={option.value}></option>{/each}
</datalist>
{#if concepts.length}
  <datalist id="mapping-target-concepts">
    {#each concepts as concept}<option value={concept.id}>{concept.label}{concept.aliases?.length ? ` · ${concept.aliases.join(', ')}` : ''}</option>{/each}
  </datalist>
{/if}

<div class="wf2-tablewrap">
  <table class="wf2-table">
    <thead><tr><th>In the file</th><th>Published as</th><th><span class="wf2-caption">Remove</span></th></tr></thead>
    <tbody>
      {#each rows as [from, to], at}
        <tr>
          <td>
            <input class="wf2-control" aria-label="Source value {at + 1}" value={from} list="mapping-source-values"
                   oninput={(e) => setRow(at, e.currentTarget.value, to)} />
          </td>
          <td>
            <input class="wf2-control" aria-label="Published value {at + 1}" value={to}
                   list={concepts.length ? 'mapping-target-concepts' : undefined}
                   oninput={(e) => setRow(at, from, e.currentTarget.value)} />
          </td>
          <td><button class="step-icon" aria-label="Remove match {at + 1}" onclick={() => removeRow(at)}>×</button></td>
        </tr>
      {:else}
        <tr><td colspan="3" class="wf2-caption">No matches yet — every value falls through to the fallback below.</td></tr>
      {/each}
    </tbody>
  </table>
</div>

<Button variant="ghost" onclick={addRow}>Add a match</Button>

{#if blank}<p class="wf2-caption">A match with no source value is ignored until you fill it in.</p>{/if}
{#if duplicate}<p class="wf2-caption" style="color:var(--danger)">Two matches share the same source value; only the last one will apply.</p>{/if}

<style>
  .mapping-options { margin-bottom: 12px; }
  .value-chip {
    border: 1px solid var(--border); border-radius: 999px; background: var(--surface);
    color: inherit; padding: 5px 9px; cursor: pointer;
  }
  .value-chip:disabled { cursor: default; opacity: .6; }
</style>
